import { useEffect } from "react";
import {
  Crown, ArrowRight, Sparkles, Check, X, MapPin, Calendar, Gift, Users,
  DollarSign, TrendingUp, Target, Award, Coffee, Heart, Plane, ShieldCheck,
  Phone, Star,
} from "lucide-react";

const Stat = ({ value, label, kicker }) => (
  <div className="text-center">
    {kicker && <p className="text-[10px] text-amber-300 font-mono uppercase tracking-[2px] mb-1">{kicker}</p>}
    <p className="text-3xl sm:text-5xl font-black text-white leading-none">{value}</p>
    <p className="text-[10px] sm:text-xs text-slate-400 uppercase tracking-wider mt-2">{label}</p>
  </div>
);

const Etapa = ({ n, title, desc, accent = "amber" }) => (
  <div className="bg-slate-900/40 border border-white/10 rounded-2xl p-5 sm:p-6 relative overflow-hidden">
    <div className={`absolute top-3 right-3 text-5xl sm:text-7xl font-black opacity-10 text-${accent}-400 leading-none`}>{n}</div>
    <div className="relative">
      <div className={`inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[2px] bg-${accent}-400/15 text-${accent}-300 border border-${accent}-400/30 rounded-full px-2.5 py-1 mb-3`}>
        Etapa {n}
      </div>
      <h3 className="text-lg sm:text-xl font-black text-white mb-2 leading-tight">{title}</h3>
      <p className="text-sm text-slate-400 leading-relaxed">{desc}</p>
    </div>
  </div>
);

const Regalo = ({ icon: Icon, title, desc, valor }) => (
  <div className="bg-slate-900/40 border border-white/10 hover:border-amber-400/40 rounded-xl p-4 transition-all hover:-translate-y-0.5">
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-lg bg-amber-400/15 border border-amber-400/30 flex items-center justify-center flex-shrink-0">
        <Icon className="h-5 w-5 text-amber-300" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <p className="text-sm font-bold text-white leading-tight">{title}</p>
          <span className="text-xs font-mono font-black text-amber-300 whitespace-nowrap">~${valor}</span>
        </div>
        <p className="text-[11px] text-slate-400 leading-snug mt-1">{desc}</p>
      </div>
    </div>
  </div>
);

const TABLA_INGRESOS = [
  { tours: 5,  sem: "$1,200", mes: "$6,000",  anio: "$72,000"  },
  { tours: 10, sem: "$3,300", mes: "$13,500", anio: "$162,000" },
  { tours: 15, sem: "$5,250", mes: "$21,000", anio: "$252,000" },
  { tours: 20, sem: "$5,700", mes: "$28,500", anio: "$342,000" },
  { tours: 25, sem: "$9,000", mes: "$36,000", anio: "$432,000" },
  { tours: 30, sem: "$8,700", mes: "$43,500", anio: "$522,000", highlight: true },
];

const REGALOS = [
  { icon: Plane,  title: "5 días en Cancún · todo incluido",         desc: "Hotel todo incluido para 2. Vuelos no incluidos. Válido 18 meses.", valor: 900 },
  { icon: Plane,  title: "5 días en Punta Cana · todo incluido",      desc: "Resort para 2 personas. Playas más buscadas de América.",          valor: 850 },
  { icon: Plane,  title: "4 días crucero Bahamas · Carnival",         desc: "Crucero para 2 desde Miami o Port Canaveral. Camarote interior.",  valor: 700 },
  { icon: Plane,  title: "2 tickets aéreos domésticos USA",           desc: "Certificado para 2 vuelos dentro de Estados Unidos.",              valor: 500 },
  { icon: Sparkles, title: "3 noches en Las Vegas",                   desc: "Hotel en la franja de Las Vegas para 2 personas.",                  valor: 450 },
  { icon: Star,   title: "2 tickets Universal Studios",               desc: "Entradas Universal Orlando o Hollywood. Alto impacto familiar.",    valor: 280 },
  { icon: Star,   title: "2 tickets SeaWorld Orlando",                desc: "Ideal para familias con niños.",                                    valor: 220 },
  { icon: Gift,   title: "Smart TV 50 pulgadas",                       desc: "Televisor smart de alta demanda. Impacto visual enorme.",          valor: 400 },
  { icon: Heart,  title: "Spa day para 2 personas",                    desc: "Masajes, tratamientos faciales y sauna en hotel de lujo.",         valor: 300 },
  { icon: DollarSign, title: "Gift card de $200 gasolina",            desc: "Tarjeta prepagada — muy apreciada por familias con autos.",        valor: 200 },
  { icon: Coffee, title: "Cena para 4 — restaurante",                  desc: "Gift card Cheesecake Factory, Olive Garden o similar.",            valor: 150 },
  { icon: DollarSign, title: "Vale de $100 Visa prepagada",           desc: "Tarjeta prepagada de uso libre en cualquier comercio.",            valor: 100 },
];

const OpcPage = () => {
  useEffect(() => {
    document.title = "Proyecto OPC · ClubViajemos";
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0a14] via-[#0d0d1c] to-[#0a0a14] text-white" style={{ fontFamily: "'DM Sans', system-ui, sans-serif" }} data-testid="opc-page">
      {/* TOP BANNER */}
      <div className="bg-gradient-to-r from-amber-400 via-amber-500 to-amber-400 text-slate-900 text-center py-2 text-xs sm:text-sm font-black tracking-wider sticky top-0 z-40">
        <MapPin className="inline h-3.5 w-3.5 mr-1.5 mb-0.5" />
        PROYECTO OPC · CLUB VIAJEMOS · FLORIDA 2025
      </div>

      {/* HERO */}
      <section className="relative px-4 pt-16 pb-12 sm:pt-24 sm:pb-16 overflow-hidden">
        <div className="absolute top-0 right-0 w-[700px] h-[700px] bg-amber-400/10 rounded-full blur-[180px] pointer-events-none" />

        <div className="relative max-w-5xl mx-auto text-center">
          <p className="inline-block bg-amber-400/15 text-amber-300 text-[11px] uppercase tracking-[3px] px-4 py-1.5 rounded-full font-mono mb-6 border border-amber-400/30">
            Outside Public Contact
          </p>

          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black leading-[0.95] tracking-tight mb-5">
            Tú conectas.<br />
            <span className="text-amber-300">Nosotros cerramos.</span><br />
            Tú cobras.
          </h1>

          <p className="text-base sm:text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed mb-2">
            El modelo de captación de prospectos más probado de la industria del turismo, ahora integrado a los Certificados de Viaje de Club Viajemos.
          </p>
          <p className="text-base sm:text-lg text-amber-200 max-w-3xl mx-auto mb-10 font-bold">
            <span className="bg-amber-400/15 px-2 rounded">$300 por cada familia que presentas</span> — pago semanal, sin techo de comisiones.
          </p>

          <div className="grid grid-cols-3 gap-4 sm:gap-8 max-w-3xl mx-auto py-7 border-y border-white/10">
            <Stat value="$300" label="Por familia presentada" />
            <Stat value="100%" label="Comisión desde el tour 2" />
            <Stat value="$522K" label="Potencial anual" />
          </div>

          <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="https://wa.me/19548059212?text=Hola%2C%20quiero%20info%20del%20Proyecto%20OPC"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="opc-cta-whatsapp-hero"
              className="inline-flex items-center justify-center gap-2.5 bg-gradient-to-r from-amber-400 to-amber-300 hover:from-amber-300 hover:to-amber-400 text-slate-900 font-black px-7 py-4 rounded-xl uppercase tracking-wider text-sm hover:scale-[1.03] transition-transform shadow-[0_15px_50px_rgba(212,175,55,0.4)]"
            >
              <Phone className="h-5 w-5" /> Quiero ser OPC
            </a>
            <a
              href="/registrosopc"
              data-testid="opc-cta-registro-tours"
              className="inline-flex items-center justify-center gap-2.5 bg-white/10 border border-amber-400/40 hover:bg-amber-400/15 text-amber-200 font-black px-7 py-4 rounded-xl uppercase tracking-wider text-sm transition-all"
            >
              <MapPin className="h-5 w-5" /> Registro de Tours
            </a>
            <a
              href="#funcionamiento"
              className="inline-flex items-center justify-center gap-2.5 bg-white/5 border border-white/20 hover:bg-white/10 text-white font-bold px-7 py-4 rounded-xl uppercase tracking-wider text-sm transition-all"
            >
              ¿Cómo funciona? <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      {/* QUÉ ES */}
      <section className="px-4 py-12 sm:py-16">
        <div className="max-w-4xl mx-auto bg-gradient-to-br from-slate-900/80 to-slate-950/80 border border-amber-400/30 rounded-3xl p-8 sm:p-12">
          <p className="text-[11px] text-amber-400 font-mono tracking-[3px] uppercase mb-3">¿Qué es el OPC?</p>
          <h2 className="text-3xl sm:text-4xl font-black mb-5 leading-tight">El sistema más probado de captación en turismo</h2>
          <div className="space-y-4 text-slate-300 text-base sm:text-lg leading-relaxed">
            <p>
              OPC (<strong className="text-amber-200">Outside Public Contact</strong>) es el método más usado en la industria de viajes y vacation clubs del mundo. Consiste en identificar y contactar personas calificadas <em>fuera del hotel</em>, e invitarlas a conocer el producto bajo el programa <strong>tour only</strong> (visita sin hospedaje).
            </p>
            <p className="border-l-4 border-amber-400 pl-5 italic text-amber-200 text-lg sm:text-xl">
              "El OPC no vende. Conecta. La familia visita, el equipo de ventas hace el resto, y tú cobras."
            </p>
          </div>
        </div>
      </section>

      {/* CÓMO FUNCIONA — 4 ETAPAS */}
      <section id="funcionamiento" className="px-4 py-12 sm:py-16">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-[11px] text-amber-400 font-mono tracking-[3px] uppercase mb-2">Cómo funciona</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black leading-tight">
              4 etapas. Un solo flujo.<br />
              <span className="text-amber-300">Resultados semana a semana.</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-5" data-testid="opc-etapas">
            <Etapa n="1" title="Identifica familias calificadas en Florida" desc="Vecinos, compañeros de trabajo, contactos en redes, grupos comunitarios, iglesias y eventos locales. Familias con ingreso estable, entre 25 y 65 años, residentes de Florida." />
            <Etapa n="2" title="Invita con el regalo como anzuelo" desc="No invitas a una presentación de ventas — invitas a conocer el hotel en Orlando o Miami y a recoger su regalo. El regalo es el motivador: tangible, real y de alto valor percibido." />
            <Etapa n="3" title="Confirman el tour y se presentan" desc="La familia llega al hotel, realiza el recorrido (tour only — sin hospedaje), asiste a la presentación del certificado de viaje y recoge su regalo al finalizar." />
            <Etapa n="4" title="Tú cobras $300 por familia presentada" desc="Pago independientemente de si compran o no. Tu trabajo es asegurar que la familia llegue al hotel. El equipo de ventas se encarga del cierre. Pagos semanales, sin techo." />
          </div>
        </div>
      </section>

      {/* MODELO DE REPARTO SEMANAL */}
      <section className="px-4 py-12 sm:py-16 bg-gradient-to-b from-transparent via-amber-400/[0.03] to-transparent">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-[11px] text-amber-400 font-mono tracking-[3px] uppercase mb-2">Modelo de reparto</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black leading-tight">
              Semana arranca cada <span className="text-amber-300">lunes</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-5 mb-7">
            <div className="rounded-2xl bg-slate-900/40 border border-amber-400/30 p-6 sm:p-7">
              <p className="text-[10px] font-mono uppercase tracking-[2px] text-amber-300 mb-2">Primer tour de la semana</p>
              <h3 className="text-xl sm:text-2xl font-black text-white mb-2">"De la casa"</h3>
              <p className="text-sm text-slate-400 leading-relaxed">
                Cubre los costos operativos del programa: regalos premium, logística y sala de presentación. Sin este soporte, no habría incentivo para atraer familias.
              </p>
            </div>
            <div className="rounded-2xl bg-gradient-to-br from-emerald-500/15 to-emerald-400/5 border border-emerald-400/40 p-6 sm:p-7">
              <p className="text-[10px] font-mono uppercase tracking-[2px] text-emerald-300 mb-2">Tours 2 en adelante</p>
              <h3 className="text-xl sm:text-2xl font-black text-white mb-2">100% TUYO</h3>
              <p className="text-sm text-slate-200 leading-relaxed">
                Cada $300 va directo a tu bolsillo — sin descuento, sin split, sin techo. Solo entregar familias al hotel.
              </p>
            </div>
          </div>

          {/* TABLA DE INGRESOS */}
          <div className="rounded-2xl overflow-hidden border border-white/10 bg-slate-900/40">
            <div className="grid grid-cols-4 bg-slate-800/60 text-[10px] sm:text-xs font-mono uppercase tracking-wider text-slate-400 font-bold">
              <div className="p-4">Tours/semana</div>
              <div className="p-4 text-right">Ingreso semanal</div>
              <div className="p-4 text-right">Ingreso mensual</div>
              <div className="p-4 text-right text-amber-400">Ingreso anual</div>
            </div>
            {TABLA_INGRESOS.map((row) => (
              <div
                key={row.tours}
                className={`grid grid-cols-4 text-sm border-t border-white/5 ${
                  row.highlight ? "bg-gradient-to-r from-amber-400/15 via-amber-500/10 to-amber-400/15" : ""
                }`}
                data-testid={`opc-row-${row.tours}`}
              >
                <div className={`p-4 font-bold ${row.highlight ? "text-amber-300" : "text-white"}`}>{row.tours}</div>
                <div className="p-4 text-right text-slate-300">{row.sem}</div>
                <div className="p-4 text-right text-slate-300">{row.mes}</div>
                <div className={`p-4 text-right font-black ${row.highlight ? "text-amber-300" : "text-emerald-300"}`}>{row.anio}</div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-slate-500 text-center mt-4 italic">
            * Pagos semanales. Cada lunes el contador se reinicia. No hay acumulación de tours entre semanas.
          </p>
        </div>
      </section>

      {/* REGALOS — EL CORAZÓN */}
      <section className="px-4 py-12 sm:py-16">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-[11px] text-amber-400 font-mono tracking-[3px] uppercase mb-2">El corazón de la invitación</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black leading-tight mb-3">
              Regalos con valor <span className="text-amber-300">real y aspiracional</span>
            </h2>
            <p className="text-slate-300 text-sm sm:text-base max-w-2xl mx-auto">
              No es una rifa. No es un sorteo. Es un intercambio justo por el tiempo de la familia.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="opc-regalos">
            {REGALOS.map((r) => <Regalo key={r.title} {...r} />)}
          </div>
        </div>
      </section>

      {/* ¿QUIÉN CALIFICA? + ¿DÓNDE LOS ENCUENTRAS? */}
      <section className="px-4 py-12 sm:py-16 bg-gradient-to-b from-transparent via-amber-400/[0.03] to-transparent">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-6">
          {/* Califica */}
          <div className="rounded-2xl bg-slate-900/40 border-2 border-emerald-400/30 p-7 sm:p-8">
            <div className="flex items-center gap-2 mb-5">
              <Target className="h-6 w-6 text-emerald-400" />
              <h3 className="text-xl sm:text-2xl font-black">¿Quién califica?</h3>
            </div>
            <ul className="space-y-3 text-sm text-slate-300">
              {[
                "Residentes del estado de Florida (cualquier ciudad)",
                "Pareja o familia — mínimo 2 adultos asistiendo juntos",
                "Entre 25 y 65 años de edad",
                "Ingreso familiar estable (empleo o negocio propio)",
                "Sin visitas a presentaciones de vacation club en los últimos 6 meses",
                "Disponibilidad de 90 minutos para el tour",
              ].map((it) => (
                <li key={it} className="flex gap-3"><Check className="h-4 w-4 text-emerald-400 flex-shrink-0 mt-1" /><span>{it}</span></li>
              ))}
            </ul>
          </div>
          {/* Fuentes */}
          <div className="rounded-2xl bg-slate-900/40 border-2 border-amber-400/30 p-7 sm:p-8">
            <div className="flex items-center gap-2 mb-5">
              <Users className="h-6 w-6 text-amber-400" />
              <h3 className="text-xl sm:text-2xl font-black">¿Dónde los encuentras?</h3>
            </div>
            <ul className="space-y-3 text-sm text-slate-300">
              {[
                { titulo: "Tu red personal", desc: "Familia, amigos, vecinos, compañeros, iglesia, grupos hispanos. La fuente más calificada." },
                { titulo: "Redes sociales", desc: "Instagram, Facebook, TikTok, Nextdoor con un gancho irresistible." },
                { titulo: "Lugares de alto tráfico", desc: "Malls, supermercados, eventos comunitarios, ferias y festivales hispanos." },
                { titulo: "Red de OPCs", desc: "Invita a otros a ser OPCs — más OPCs, más familias, más ingresos consistentes." },
              ].map((it) => (
                <li key={it.titulo} className="flex gap-3">
                  <Star className="h-4 w-4 text-amber-400 flex-shrink-0 mt-1" />
                  <div>
                    <p className="font-bold text-white">{it.titulo}</p>
                    <p className="text-xs text-slate-400">{it.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* RESUMEN — POR QUÉ FUNCIONA */}
      <section className="px-4 py-12 sm:py-16">
        <div className="max-w-4xl mx-auto rounded-3xl bg-gradient-to-br from-slate-900/80 to-slate-950/80 border border-amber-400/30 p-8 sm:p-12 relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-80 h-80 bg-amber-400/15 rounded-full blur-3xl pointer-events-none" />
          <div className="relative text-center">
            <p className="text-[11px] text-amber-400 font-mono tracking-[3px] uppercase mb-3">El trato simple</p>
            <div className="space-y-3 mb-7">
              <p className="text-3xl sm:text-5xl font-black">Tú <span className="text-amber-300">conectas</span>.</p>
              <p className="text-3xl sm:text-5xl font-black">Nosotros <span className="text-emerald-300">cerramos</span>.</p>
              <p className="text-3xl sm:text-5xl font-black">Tú <span className="text-amber-300">cobras</span>.</p>
            </div>
            <div className="grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
              <div className="rounded-xl bg-slate-900/60 border border-white/10 p-4">
                <Award className="h-6 w-6 text-amber-300 mx-auto mb-2" />
                <p className="text-xs text-slate-300">Sin experiencia en ventas</p>
              </div>
              <div className="rounded-xl bg-slate-900/60 border border-white/10 p-4">
                <ShieldCheck className="h-6 w-6 text-emerald-300 mx-auto mb-2" />
                <p className="text-xs text-slate-300">Sin capital inicial</p>
              </div>
              <div className="rounded-xl bg-slate-900/60 border border-white/10 p-4">
                <TrendingUp className="h-6 w-6 text-amber-300 mx-auto mb-2" />
                <p className="text-xs text-slate-300">Solo conocer personas</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="px-4 py-12 sm:py-16">
        <div className="max-w-3xl mx-auto rounded-3xl overflow-hidden bg-gradient-to-br from-amber-500/15 via-slate-900/60 to-slate-950 border-2 border-amber-400/40 p-8 sm:p-12 text-center relative">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(212,175,55,0.15),transparent_60%)] pointer-events-none" />
          <div className="relative">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full border-2 border-amber-400 bg-amber-400/10 mb-5">
              <Crown className="h-10 w-10 text-amber-300" />
            </div>
            <p className="text-[11px] text-amber-300 font-mono tracking-[3px] uppercase mb-2">¿Estás listo?</p>
            <h3 className="text-3xl sm:text-4xl font-black mb-4 leading-tight">
              Empieza a generar ingresos<br /><span className="text-amber-300">esta misma semana</span>
            </h3>
            <p className="text-slate-300 text-base leading-relaxed mb-7 max-w-xl mx-auto">
              Si vives en Florida, conoces gente y quieres construir un ingreso semanal — este modelo está hecho para ti.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="https://wa.me/19548059212?text=Hola%2C%20quiero%20unirme%20al%20Proyecto%20OPC"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="opc-cta-whatsapp-final"
                className="inline-flex items-center justify-center gap-2.5 bg-gradient-to-r from-amber-400 to-amber-300 hover:from-amber-300 hover:to-amber-400 text-slate-900 font-black px-8 py-4 rounded-xl uppercase tracking-wider text-base hover:scale-[1.03] transition-transform shadow-[0_15px_50px_rgba(212,175,55,0.4)]"
              >
                <Phone className="h-5 w-5" /> Quiero ser OPC · WhatsApp
              </a>
              <a
                href="/precios"
                className="inline-flex items-center justify-center gap-2.5 bg-white/5 border border-white/20 hover:bg-white/10 text-white font-bold px-8 py-4 rounded-xl uppercase tracking-wider text-base transition-all"
              >
                <Crown className="h-5 w-5" /> Ver planes
              </a>
            </div>
            <p className="text-xs text-slate-400 mt-6 flex items-center gap-1.5 justify-center">
              <Calendar className="h-3 w-3" /> Webinars: miércoles 8:45 PM &amp; sábados 10:30 AM (hora Miami)
            </p>
            <p className="text-xs mt-3">
              <a href="/opc/terminos" data-testid="opc-link-terminos" className="text-amber-400 hover:text-amber-300 underline decoration-amber-400/40 underline-offset-2">
                Ver términos y condiciones del Módulo OPC / DPC
              </a>
            </p>
          </div>
        </div>
      </section>

      <footer className="py-10 px-4 text-center text-xs text-slate-500 border-t border-white/5">
        <p>&copy; 2026 Club Viajemos / Travel Empire · Florida 2025</p>
        <p className="mt-1">+5,400 agencias activas · Modelo OPC: Outside Public Contact</p>
        <p className="mt-3">
          <a href="/" className="text-amber-400 hover:underline">← Volver al sitio principal</a>
        </p>
      </footer>
    </div>
  );
};

export default OpcPage;
